import React from "react";
import { ArrowRight } from "lucide-react";
import { POPULAR_COMBOS as FALLBACK } from "../data/site";
import { useSiteContent } from "../data/SiteContentContext";

export default function PopularCombos() {
  const { combos: liveCombos, content } = useSiteContent();
  const combos = liveCombos && liveCombos.length ? liveCombos : FALLBACK;
  const ORDER_URL = content?.links?.order_url ||
    "https://www.grubhub.com/restaurant/burgers-tacos-wings--seafood-110-46-merrick-blvd-jamaica/2508348";

  return (
    <section
      id="combos"
      data-testid="popular-combos-section"
      className="py-12 sm:py-24 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #1f2227 0%, #16181c 50%, #0f1114 100%)",
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 360px at 50% -10%, rgba(95,189,214,0.08), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionTitle title="POPULAR COMBOS" dark />
        <p className="mt-3 text-center text-[10px] sm:text-xs tracking-[0.3em] uppercase text-white/45">
          In-store pricing
        </p>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {combos.map((combo) => (
            <article
              key={combo.id}
              data-testid={`combo-card-${combo.id}`}
              className="relative bg-white rounded-2xl border border-black/5 shadow-[0_10px_40px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_50px_rgba(0,0,0,0.5)] hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
            >
              {combo.badge && (
                <span
                  className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 rounded-full bg-[#5FBDD6] text-[#0B0B0B] text-[8px] sm:text-[10px] font-extrabold tracking-[0.18em] uppercase px-2 py-1 sm:px-3 sm:py-1.5"
                  data-testid={`combo-badge-${combo.id}`}
                >
                  {combo.badge}
                </span>
              )}
              <div className="relative aspect-square sm:aspect-[4/3] overflow-hidden bg-[#0B0B0B]">
                <img
                  src={combo.img}
                  alt={combo.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="px-3 pt-3 pb-4 sm:px-6 sm:pt-5 sm:pb-6 flex flex-col flex-1 text-center">
                <h3 className="font-display text-base sm:text-2xl uppercase text-[#0B0B0B] tracking-tight leading-tight">
                  {combo.name}
                </h3>
                <p className="mt-1 sm:mt-2 text-[11px] sm:text-sm text-black/60 font-medium leading-snug line-clamp-2">
                  {combo.desc}
                </p>
                <div
                  className="font-display text-2xl sm:text-4xl mt-2 sm:mt-4"
                  style={{ color: "#5FBDD6" }}
                >
                  {combo.price}
                </div>
                <div className="mt-3 sm:mt-5 flex flex-col sm:flex-row gap-2 sm:gap-2.5">
                  <a
                    href={ORDER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`combo-order-${combo.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.14em] sm:tracking-[0.18em] uppercase px-3 py-2 sm:px-5 sm:py-3 text-[10px] sm:text-xs transition-colors"
                  >
                    Order Now
                    <ArrowRight size={12} className="hidden sm:inline" />
                  </a>
                  <a
                    href="/menu"
                    data-testid={`combo-view-menu-${combo.id}`}
                    className="flex-1 inline-flex items-center justify-center rounded-full border-2 border-[#5FBDD6] text-[#5FBDD6] hover:bg-[#5FBDD6] hover:text-white font-extrabold tracking-[0.14em] sm:tracking-[0.18em] uppercase px-3 py-2 sm:px-5 sm:py-3 text-[10px] sm:text-xs transition-colors"
                  >
                    View Menu
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionTitle({ title, dark = false }) {
  return (
    <div className="flex items-center justify-center gap-5">
      <span
        className="hidden sm:block h-[2px] w-24 lg:w-44"
        style={{ background: "#5FBDD6" }}
      />
      <div className="flex flex-col items-center">
        <span className="text-xs tracking-[0.4em]" style={{ color: "#5FBDD6" }}>
          ★ ★ ★
        </span>
        <h2
          className={`font-display text-3xl sm:text-5xl uppercase tracking-tight mt-2 ${
            dark ? "text-white" : "text-[#0B0B0B]"
          }`}
        >
          {title}
        </h2>
      </div>
      <span
        className="hidden sm:block h-[2px] w-24 lg:w-44"
        style={{ background: "#5FBDD6" }}
      />
    </div>
  );
}
